import { formatPesoAmount } from '../utils/money';
import React, { useState, useEffect } from "react";
import ModalClientRegistration from "./ModalClientRegistration";
import DatePresetPicker from "./DatePresetPicker";
import { getPurchaseItems } from "../utils/purchaseItems";
import { notify } from '../utils/alertBus';
import useConfirmDialog from '../hooks/useConfirmDialog';
import { previewSaleRounding } from '../utils/saleRounding';

const PaymentModal = ({
    isOpen,
    onClose,
    onConfirmPayment,
    purchaseData,
    canReadClient = true,
    canCreateClient = true,
    onContinuePurchase,
    isContinuingPurchase = false,
    saleRoundingSettings,
    roundingSettingsLoading = false,
    roundingSettingsError = null,
    onRetryRoundingSettings,
}) => {
    const [purchase, setPurchase] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [paymentMethod, setPaymentMethod] = useState("");
    const [paymentDetails, setPaymentDetails] = useState({});
    const [documentType, setDocumentType] = useState(null);
    const [selectedClient, setSelectedClient] = useState(null);
    const [pendingDocumentType, setPendingDocumentType] = useState(null);
    const [showClientModal, setShowClientModal] = useState(false);
    const [name, setName] = useState("");
    const [rut, setRut] = useState("");
    const [email, setEmail] = useState("");
    const [address, setAddress] = useState("");
    const [city, setCity] = useState("");
    const [phone, setPhone] = useState("");
    const [clientType, setClientType] = useState("");
    const { confirm } = useConfirmDialog();

    const normalizeDocumentType = (value) => {
        const normalized = String(value || '').trim().toLowerCase();
        if (normalized === 'boleta' || normalized === 'factura') {
            return normalized;
        }
        return null;
    };

    useEffect(() => {
        setPurchase(purchaseData);
        setDocumentType(normalizeDocumentType(purchaseData?.documentType));
        const nextClient = purchaseData?.Client || null;
        setSelectedClient(nextClient);
        setName(nextClient?.name || "");
        setRut(nextClient?.rut || "");
        setEmail(nextClient?.email || "");
        setAddress(nextClient?.address || "");
        setCity(nextClient?.city || "");
        setPhone(nextClient?.phone || "");
        setClientType("");
        setPendingDocumentType(null);
    }, [purchaseData]);

    // Function to reset payment details when payment method changes
    const resetPaymentDetails = (nextDetails = {}) => {
        setPaymentDetails(nextDetails); // Reset all previous inputs
    };

    const paymentMethods = [
        { id: "tarjeta", label: "Tarjeta" },
        { id: "transferencia", label: "Transferencia" },
        { id: "cheque", label: "Cheque" },
        { id: "crédito", label: "Crédito" },
        { id: "efectivo", label: "Efectivo" },
    ];

    const documentTypeOptions = [
        { id: 'boleta', label: 'Boleta', requiresClient: false },
        { id: 'factura', label: 'Factura', requiresClient: true },
    ];

    const handleDocumentTypeClick = (option) => {
        if (option.requiresClient && !selectedClient) {
            setPendingDocumentType(option.id);
            setShowClientModal(true);
            return;
        }
        setDocumentType(option.id);
    };

    const handleClientPicked = (client) => {
        const resolvedClient = client || null;
        setSelectedClient(resolvedClient);
        setName(resolvedClient?.name || "");
        setRut(resolvedClient?.rut || "");
        setEmail(resolvedClient?.email || "");
        setAddress(resolvedClient?.address || "");
        setCity(resolvedClient?.city || "");
        setPhone(resolvedClient?.phone || "");
        setClientType("");
        if (pendingDocumentType) {
            setDocumentType(pendingDocumentType);
            setPendingDocumentType(null);
        }
    };

    const handleCloseClientPicker = (open) => {
        setShowClientModal(open);
        if (!open) {
            setPendingDocumentType(null);
        }
    };

    const handleMethodButtonClick = (methodId) => {
        if (paymentMethod === methodId) return;
        setPaymentMethod(methodId);
        if (methodId === "crédito") {
            resetPaymentDetails({
                creditDueDate: "",
                initialPayment: 0,
                initialPaymentMethod: "efectivo",
            });
            return;
        }
        resetPaymentDetails(); // Reset details when payment method changes
    };

    const cleanPaymentDetails = (details) => Object.entries(details || {}).reduce((acc, [key, value]) => {
        if (value === undefined || value === null) return acc;
        if (typeof value === "string" && value.trim() === "") return acc;
        acc[key] = typeof value === "string" ? value.trim() : value;
        return acc;
    }, {});

    const isOpenSale = purchase?.saleStatus === 'open';
    const roundingPreview = isOpenSale ? previewSaleRounding({
        totalAmount: purchase?.totalBeforeRounding ?? (Number(purchase?.totalAmount || 0) + Number(purchase?.roundingDiscountAmount || 0)),
        settings: saleRoundingSettings,
        checkoutPaymentMethods: paymentMethod ? [paymentMethod] : [],
        paymentTerm: paymentMethod === 'crédito' ? 'credit' : 'immediate',
    }) : purchase;
    const totalAmount = Number(roundingPreview?.totalAmount || 0);
    const roundingDiscountAmount = Number(roundingPreview?.roundingDiscountAmount || 0);
    const paidAmount = Number(purchase?.paidAmount || 0);
    const pendingAmount = isOpenSale || purchase?.balance == null
        ? Math.max(totalAmount - paidAmount, 0)
        : Number(purchase.balance || 0);
    const expectedAmount = pendingAmount > 0 ? pendingAmount : totalAmount;
    const settingsUnavailable = isOpenSale && (roundingSettingsLoading || roundingSettingsError);

    const handleConfirm = async () => {
        if (isSubmitting || settingsUnavailable) return;
        if (!paymentMethod) {
            notify("Seleccione un método de pago.");
            return;
        }

        const resolvedPaymentDetails = { ...paymentDetails };

        if (paymentMethod === "efectivo") {
            const hasReceivedAmount =
                paymentDetails.receivedAmount !== undefined
                && paymentDetails.receivedAmount !== null
                && String(paymentDetails.receivedAmount).trim() !== "";
            const received = hasReceivedAmount
                ? Number(paymentDetails.receivedAmount || 0)
                : expectedAmount;

            if (!Number.isFinite(received) || received < 0 || (received === 0 && expectedAmount > 0)) {
                notify("Ingrese el monto recibido en efectivo.");
                return;
            }

            if (received < expectedAmount) {
                const proceed = await confirm({
                    title: 'Monto insuficiente',
                    message: 'El monto recibido es menor que el total. ¿Desea continuar?',
                    confirmLabel: 'Continuar',
                    cancelLabel: 'Cancelar',
                    tone: 'warning',
                });
                if (!proceed) return;
            }
            if (hasReceivedAmount) {
                resolvedPaymentDetails.receivedAmount = received;
            } else {
                delete resolvedPaymentDetails.receivedAmount;
            }
        }

        if (paymentMethod === "crédito") {
            if (!paymentDetails.creditDueDate) {
                notify("Ingrese la fecha de vencimiento del crédito.");
                return;
            }
            const initialPayment = Number(paymentDetails.initialPayment || 0);
            if (!Number.isFinite(initialPayment) || initialPayment < 0) {
                notify("El abono inicial debe ser 0 o mayor.");
                return;
            }
            if (initialPayment > 0 && !paymentDetails.initialPaymentMethod) {
                notify("Seleccione el método del abono inicial.");
                return;
            }
        }

        if (documentType === 'factura' && !selectedClient) {
            notify('Factura requiere cliente.');
            return;
        }

        const paymentTerm = paymentMethod === "crédito" ? "credit" : "immediate";
        const resolvedClientId = selectedClient?.id ?? purchase?.clientId ?? null;
        const cleanedPaymentDetails = cleanPaymentDetails(resolvedPaymentDetails);
        setIsSubmitting(true);
        try {
            const succeeded = await onConfirmPayment(
                paymentMethod,
                cleanedPaymentDetails,
                paymentTerm,
                documentType,
                resolvedClientId,
                totalAmount
            );
            if (succeeded !== false) onClose();
        } catch (error) {
            notify(error?.response?.data?.message || 'No se pudo registrar el pago. Intenta nuevamente.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const purchaseItems = getPurchaseItems(purchase);
    const itemsSubtotal = purchaseItems.length
        ? purchaseItems.reduce((sum, item) => {
              if (!item || item.quantity <= 0) return sum;
              const unit = (item.price || 0) - (item.discount || 0);
              return sum + Math.floor(unit * item.quantity);
          }, 0)
        : totalAmount;
    const orderDiscountAmountNum = purchase?.orderDiscountAmount == null ? NaN : Number(purchase.orderDiscountAmount);
    const orderDiscountAmountRaw = Number.isFinite(orderDiscountAmountNum)
        ? orderDiscountAmountNum
        : null;
    const orderDiscountPercentNum = purchase?.orderDiscountPercentage == null ? NaN : Number(purchase.orderDiscountPercentage);
    const orderDiscountPercentage = Number.isFinite(orderDiscountPercentNum)
        ? orderDiscountPercentNum
        : null;
    const effectiveOrderDiscount = orderDiscountAmountRaw ?? (
        typeof orderDiscountPercentage === 'number'
            ? Math.floor(itemsSubtotal * (orderDiscountPercentage / 100))
            : 0
    );
    const hasCashReceivedAmount =
        paymentDetails.receivedAmount !== undefined
        && paymentDetails.receivedAmount !== null
        && String(paymentDetails.receivedAmount).trim() !== "";
    const cashReceivedAmount = hasCashReceivedAmount
        ? Number(paymentDetails.receivedAmount || 0)
        : expectedAmount;

        const client = selectedClient;
        const initialPaymentMethods = paymentMethods.filter((method) => method.id !== "crédito");
        const docLabel = (() => {
            if (documentType === 'boleta' || documentType === 'factura') {
                return documentType.charAt(0).toUpperCase() + documentType.slice(1);
        }
        return 'Factura';
    })();

    return (
        isOpen && (
            <>
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
                    onClick={(event) => event.stopPropagation()}
                >
                    <div
                        className="bg-white p-6 rounded-lg shadow-lg max-w-lg w-full max-h-[90vh] overflow-y-auto"
                        onClick={(e) => e.stopPropagation()}
                    >
                    <h2 className="text-xl font-bold mb-2">Registrar Pago</h2>
                    <div className="mb-3 text-sm bg-gray-50 border rounded p-3">
                        <p className="font-semibold">
                            {docLabel} N° {purchase?.id || '—'}
                        </p>
                        {client ? (
                            <div className="mt-1 space-y-0.5">
                                <p><span className="font-medium">Cliente:</span> {client.name || 'Sin nombre'}</p>
                                {client.rut ? <p><span className="font-medium">RUT:</span> {client.rut}</p> : null}
                                {client.email ? <p><span className="font-medium">Email:</span> {client.email}</p> : null}
                                {client.phone ? <p><span className="font-medium">Teléfono:</span> {client.phone}</p> : null}
                                {client.address ? <p><span className="font-medium">Dirección:</span> {client.address}{client.city ? `, ${client.city}` : ''}</p> : null}
                            </div>
                        ) : (
                            <p className="text-gray-700 mt-1">Venta detalle (sin cliente)</p>
                        )}
                        <div className="mt-2">
                            <button
                                type="button"
                                onClick={() => setShowClientModal(true)}
                                className="text-xs px-2 py-1 rounded border border-gray-300 text-gray-600 hover:bg-gray-100"
                                disabled={!canReadClient && !canCreateClient}
                            >
                                {client ? 'Cambiar cliente' : 'Seleccionar cliente'}
                            </button>
                            {!canReadClient && !canCreateClient && (
                                <p className="text-xs text-red-600 mt-1">
                                    No tienes permisos para buscar clientes.
                                </p>
                            )}
                        </div>
                    </div>

                    <div className="mb-2 text-sm">
                        <p className="font-medium">
                            Subtotal: ${formatPesoAmount(itemsSubtotal)}
                        </p>
                        {effectiveOrderDiscount > 0 && (
                            <p className="text-red-600">
                                Descuento orden
                                {orderDiscountPercentage != null
                                    ? ` (${orderDiscountPercentage}%)`
                                    : ''}: -${formatPesoAmount(effectiveOrderDiscount)}
                            </p>
                        )}
                        {roundingDiscountAmount > 0 && (
                            <p className="text-red-600">Descuento por redondeo: -${formatPesoAmount(roundingDiscountAmount)}</p>
                        )}
                        {roundingPreview?.requiresPaymentMethod && (
                            <p className="text-gray-600">Selecciona el método de pago para calcular el redondeo.</p>
                        )}
                        {settingsUnavailable && (
                            <p className="text-amber-700" role="status">
                                {roundingSettingsLoading ? 'Cargando configuración de redondeo…' : 'No se pudo cargar la configuración de redondeo.'}
                                {roundingSettingsError && <button type="button" onClick={onRetryRoundingSettings} className="ml-2 underline">Reintentar</button>}
                            </p>
                        )}
                        <p className="font-semibold mt-1">
                            Total: ${formatPesoAmount(totalAmount)}
                        </p>
                        {pendingAmount > 0 && (
                            <p className="font-semibold mt-1 text-amber-700">
                                Pendiente: ${formatPesoAmount(pendingAmount)}
                            </p>
                        )}
                    </div>

                    <p className="block text-sm font-medium mb-2 mt-4">Documento:</p>
                    <div className="flex flex-wrap gap-2 mb-2">
                        {documentTypeOptions.map((option) => {
                            const disabled = option.requiresClient && !client;
                            return (
                                <button
                                    key={option.id}
                                    type="button"
                                    onClick={() => handleDocumentTypeClick(option)}
                                    className={`px-3 py-1 text-sm rounded border transition-colors ${
                                        documentType === option.id
                                            ? "bg-emerald-500 text-white border-emerald-500"
                                            : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
                                    } ${disabled ? "opacity-60" : ""}`}
                                    aria-disabled={disabled}
                                >
                                    {option.label}
                                </button>
                            );
                        })}
                    </div>
                    {!client && (
                        <p className="text-xs text-gray-500 mb-3">
                            Factura requiere cliente.
                        </p>
                    )}

                    {/* Payment Method Buttons */}
                    <p className="block text-sm font-medium mb-2 mt-4">Método de Pago:</p>
                    <div className="flex flex-wrap gap-2 mb-4">
                        {paymentMethods.map((method) => (
                            <button
                                key={method.id}
                                type="button"
                                onClick={() => handleMethodButtonClick(method.id)}
                                disabled={isSubmitting}
                                className={`px-3 py-1 text-sm rounded border transition-colors ${
                                    paymentMethod === method.id
                                        ? "bg-blue-500 text-white border-blue-500"
                                        : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
                                }`}
                            >
                                {method.label}
                            </button>
                        ))}
                    </div>

                    {/* Conditional Inputs Based on Payment Method */}
                    {paymentMethod === "cheque" && (
                        <div className="mt-4 space-y-2">
                            <label className="block text-sm font-medium">Número de Cheque (opcional):</label>
                            <input
                                type="text"
                                className="w-full border rounded p-2"
                                value={paymentDetails.chequeNumber || ""}
                                onChange={(e) =>
                                    setPaymentDetails({
                                        ...paymentDetails,
                                        chequeNumber: e.target.value,
                                    })
                                }
                            />
                            <label className="block text-sm font-medium">Fecha de Expiración (opcional):</label>
                            <input
                                type="date"
                                className="w-full border rounded p-2"
                                value={paymentDetails.chequeExpiration || ""}
                                onChange={(e) =>
                                    setPaymentDetails({
                                        ...paymentDetails,
                                        chequeExpiration: e.target.value,
                                    })
                                }
                            />
                            <label className="block text-sm font-medium">Abono Inicial (opcional):</label>
                            <input
                                type="number"
                                className="w-full border rounded p-2"
                                min="0"
                                step="1"
                                value={paymentDetails.initialPayment ?? ""}
                                onChange={(e) => {
                                    const rawValue = e.target.value;
                                    setPaymentDetails({
                                        ...paymentDetails,
                                        initialPayment: rawValue === ""
                                            ? ""
                                            : Math.max(0, parseInt(rawValue, 10) || 0),
                                    });
                                }}
                            />
                        </div>
                    )}

                    {paymentMethod === "transferencia" && (
                        <div className="mt-4 space-y-2">
                            <label className="block text-sm font-medium">Número de Transacción (opcional):</label>
                            <input
                                type="text"
                                className="w-full border rounded p-2"
                                value={paymentDetails.transactionNumber || ""}
                                onChange={(e) =>
                                    setPaymentDetails({
                                        ...paymentDetails,
                                        transactionNumber: e.target.value,
                                    })
                                }
                            />
                        </div>
                    )}

                    {paymentMethod === "efectivo" && (
                        <div className="mt-4 space-y-2">
                            <label className="block text-sm font-medium">Monto Recibido (opcional):</label>
                            <input
                                type="number"
                                className="w-full border rounded p-2"
                                value={paymentDetails.receivedAmount ?? ""}
                                onChange={(e) => {
                                    const rawValue = e.target.value;
                                    const receivedAmount = rawValue === ""
                                        ? ""
                                        : Math.max(0, parseInt(rawValue, 10) || 0);
                                    setPaymentDetails({
                                        ...paymentDetails,
                                        receivedAmount,
                                    });
                                }}
                            />
                            <p className="text-sm font-semibold">
                                {!hasCashReceivedAmount
                                    ? `Pago exacto: ${formatPesoAmount(expectedAmount)}`
                                    : cashReceivedAmount - expectedAmount > 0
                                        ? `Vuelto: ${formatPesoAmount(cashReceivedAmount - expectedAmount)}`
                                        : `Faltan: ${formatPesoAmount(Math.abs(expectedAmount - cashReceivedAmount))}`}
                            </p>
                        </div>
                    )}

                    {paymentMethod === "crédito" && (
                        <div className="mt-4 space-y-2">
                            <DatePresetPicker
                                label="Fecha de vencimiento del crédito"
                                presetLabel="Plazo"
                                inputLabel="Fecha personalizada"
                                presetOptionLabel={(days) => `En ${days} dias`}
                                value={paymentDetails.creditDueDate || ""}
                                onChange={(value) =>
                                    setPaymentDetails({
                                        ...paymentDetails,
                                        creditDueDate: value,
                                    })
                                }
                                containerClassName="w-full"
                                labelClassName="block text-sm font-medium mb-1"
                                inputLabelClassName="block text-xs text-gray-600 mb-1"
                                selectClassName="w-full border rounded p-2"
                                inputClassName="w-full border rounded p-2"
                            />
                            <label className="block text-sm font-medium">Abono Inicial:</label>
                            <input
                                type="number"
                                className="w-full border rounded p-2"
                                min="0"
                                step="1"
                                value={paymentDetails.initialPayment ?? 0}
                                onChange={(e) =>
                                    setPaymentDetails({
                                        ...paymentDetails,
                                        initialPayment: Math.max(0, parseInt(e.target.value, 10) || 0),
                                    })
                                }
                            />
                            <label className="block text-sm font-medium">Método del abono inicial:</label>
                            <select
                                className="w-full border rounded p-2"
                                value={paymentDetails.initialPaymentMethod || "efectivo"}
                                onChange={(e) =>
                                    setPaymentDetails({
                                        ...paymentDetails,
                                        initialPaymentMethod: e.target.value,
                                    })
                                }
                            >
                                {initialPaymentMethods.map((method) => (
                                    <option key={method.id} value={method.id}>
                                        {method.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {paymentMethod === "tarjeta" && (
                        <div className="mt-4 space-y-2">
                            <label className="block text-sm font-medium">Número de Transacción (opcional):</label>
                            <input
                                type="text"
                                className="w-full border rounded p-2"
                                value={paymentDetails.transactionNumber || ""}
                                onChange={(e) =>
                                    setPaymentDetails({
                                        ...paymentDetails,
                                        transactionNumber: e.target.value,
                                    })
                                }
                            />
                        </div>
                    )}

                    {/* Buttons */}
                    <div className="flex justify-end gap-4">
                        {onContinuePurchase && (
                            <button
                                type="button"
                                onClick={onContinuePurchase}
                                disabled={isContinuingPurchase || isSubmitting}
                                className={`px-4 py-2 border border-gray-300 rounded ${
                                    isContinuingPurchase
                                        ? "bg-gray-100 text-gray-500 cursor-not-allowed"
                                        : "bg-gray-200 hover:bg-gray-300"
                                }`}
                            >
                                {isContinuingPurchase ? "Continuando..." : "Continuar Compra"}
                            </button>
                        )}
                        <button
                            onClick={handleConfirm}
                            disabled={isSubmitting || Boolean(settingsUnavailable)}
                            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                        >
                            {isSubmitting ? "Registrando pago…" : "Confirmar Pago"}
                        </button>
                    </div>
                    </div>
                </div>
                <ModalClientRegistration
                    isOpen={showClientModal}
                    onClose={handleCloseClientPicker}
                    name={name}
                    setName={setName}
                    setClientId={() => {}}
                    rut={rut}
                    setRut={setRut}
                    email={email}
                    setEmail={setEmail}
                    address={address}
                    setAddress={setAddress}
                    city={city}
                    setCity={setCity}
                    phone={phone}
                    setPhone={setPhone}
                    savePurchase={() => true}
                    setModalVarietyPrice={() => {}}
                    onClientSelected={handleClientPicked}
                    clientType={clientType}
                    setClientType={setClientType}
                    allowContinueWithoutClient={false}
                    requireClient={pendingDocumentType === 'factura'}
                    canCreateClient={canCreateClient}
                    canReadClient={canReadClient}
                    detailActionLabel="Cerrar"
                    cancelLabel="Cancelar"
                />
            </>
        )
    );
};

export default PaymentModal;
